import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {DiffUser} from '@github-ui/diff-lines/types'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import type {Repository} from '@github-ui/current-repository'
import type {PullRequestFileTreeDiff} from '../page-data/payloads/file-tree'
import {EmptyPullRequestBlankSlate} from './EmptyPullRequestBlankSlate'
import {
  useActiveChangeGroup,
  useDiffEntries,
  useDiffEntriesWithFilter,
  useDiffSummaries,
  useDiffSummaryCount,
  useGetCollapsedDiffStatus,
  useProgressiveDiffActions,
} from '../stores/selectors'
import {useScrollToAndFocus} from '../hooks/use-scroll-to-and-focus'
import {Link} from '@primer/react'
import type {DiffFilterState} from '../stores/slices/diff-filter'
import {useWindowVirtualizer} from '@tanstack/react-virtual'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import {PullRequestDiffsAnimation} from './PullRequestDiffsAnimation'
import {VirtualizedDiffRow} from './PullRequestVirtualizedDiffRow'
import virtualizedStyles from './PullRequestVirtualizedDiffsList.module.css'
import {CommentsPreference, DiffLineSpacingPreference} from '@github-ui/diff-view-settings/types'
import {estimateDiffSize} from '../helpers/estimate-diff-size'

export interface PullRequestVirtualizedDiffsListProps {
  basePath: string
  currentUser: DiffUser
  filteredDiffSummaries: PullRequestFileTreeDiff[]
  headBranchName: string
  headRepoName?: string
  headRepoOwner?: string
  onSetFilterState: (updatedDiffFilterState: Partial<DiffFilterState>) => void
  pullRequestGlobalRelayId: string
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  showPullRequestGroups?: boolean
}

export function PullRequestVirtualizedDiffsList({
  basePath,
  currentUser,
  filteredDiffSummaries,
  headBranchName,
  headRepoName,
  headRepoOwner,
  onSetFilterState,
  pullRequestGlobalRelayId,
  repository,
  showPullRequestGroups,
}: PullRequestVirtualizedDiffsListProps) {
  'use no memo'

  const diffsParentRef = useRef<HTMLDivElement | null>(null)
  const [scrollMargin, setScrollMargin] = useState(0)
  const filteredDiffEntries = useDiffEntriesWithFilter(filteredDiffSummaries)
  const activeChangeGroup = useActiveChangeGroup()
  const {startQueue, loadMore} = useProgressiveDiffActions()

  const {selectedPathDigest} = useScrollToAndFocus()

  const diffSummariesCount = useDiffSummaryCount()
  const allDiffSummaries = useDiffSummaries()
  const diffEntries = useDiffEntries()

  const totalVisibleEntries = filteredDiffEntries.length
  const shouldShowWhimsy = totalVisibleEntries >= 15
  const getCollapsedDiffStatus = useGetCollapsedDiffStatus()

  useEffect(() => {
    startQueue()
  }, [startQueue])

  /**
   * Estimate size in pixels of each diff entry.
   */
  const estimateVirtualDiffSize = useCallback(
    (index: number) => {
      const progressiveDiffEntry = filteredDiffEntries[index]
      if (!progressiveDiffEntry) return 0

      const diffSummary = allDiffSummaries[progressiveDiffEntry.path ?? '']
      const isCollapsed = getCollapsedDiffStatus(progressiveDiffEntry.path ?? '')

      return estimateDiffSize({
        diffSummary,
        commentPreference: currentUser?.commentsPreference ?? CommentsPreference.Visible,
        lineSpacingPreference: currentUser?.lineSpacing ?? DiffLineSpacingPreference.Relaxed,
        isCollapsed,
      })
    },
    [
      filteredDiffEntries,
      allDiffSummaries,
      getCollapsedDiffStatus,
      currentUser?.commentsPreference,
      currentUser?.lineSpacing,
    ],
  )

  const getItemKey = useCallback(
    (index: number) => filteredDiffEntries[index]?.pathDigest ?? index,
    [filteredDiffEntries],
  )

  useEffect(() => {
    if (diffsParentRef.current) {
      setScrollMargin(diffsParentRef.current.offsetTop)
    }
  }, [])

  const virtualizer = useWindowVirtualizer({
    count: filteredDiffEntries.length,
    estimateSize: estimateVirtualDiffSize,
    getItemKey,
    overscan: 5,
    scrollMargin,
    gap: 16,
  })

  const virtualItems = virtualizer.getVirtualItems()

  const firstUnloadedIndex = useMemo(() => {
    for (const item of virtualItems) {
      const entry = filteredDiffEntries[item.index]
      if (entry?.loadingStatus === 'NotLoaded') {
        return item.index
      }
    }
    return -1
  }, [virtualItems, filteredDiffEntries])

  useEffect(() => {
    if (firstUnloadedIndex === -1) return

    const entry = filteredDiffEntries[firstUnloadedIndex]
    if (!entry) return

    loadMore(entry)
  }, [firstUnloadedIndex, filteredDiffEntries, loadMore])

  /*
   * Empty state for PRs with no changed files.
   * See below for separate empty state for empty filter results.
   */
  if (diffSummariesCount === 0) {
    return <EmptyPullRequestBlankSlate />
  }
  /*
   * Empty state
   * Show a blank slate if nothing matches currently applied filters.
   */
  if (filteredDiffEntries.length === 0) {
    return <FilesChangedFilterBlankSlate />
  }

  return (
    <div ref={diffsParentRef} data-hpc data-testid="virtualized-diffs-list" className="d-flex flex-column gap-3">
      {showPullRequestGroups && activeChangeGroup ? (
        <>
          <div className="f4 text-bold mb-0">
            <span className="fgColor-accent">
              <Link
                href=""
                onClick={e => {
                  e.preventDefault()
                  onSetFilterState({activeChangeGroupId: undefined})
                }}
              >
                All files
              </Link>
            </span>{' '}
            <span className="fgColor-muted text-normal">/</span> {activeChangeGroup.name}
          </div>
          {activeChangeGroup.description?.trim() ? <p>{activeChangeGroup.description}</p> : null}
        </>
      ) : null}
      <div
        className={virtualizedStyles['virtualized-diff-row']}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems.map(virtualRow => {
          const diffEntry = filteredDiffEntries[virtualRow.index]
          return (
            <VirtualizedDiffRow
              key={virtualRow.key}
              virtualRow={virtualRow}
              virtualizedDiffEntry={diffEntry}
              diffEntry={diffEntries[diffEntry?.path ?? '']}
              diffSummary={allDiffSummaries[diffEntry?.path ?? '']}
              scrollMargin={virtualizer.options.scrollMargin}
              measureElement={virtualizer.measureElement}
              basePath={basePath}
              currentUser={currentUser}
              headBranchName={headBranchName}
              headRepoName={headRepoName}
              headRepoOwner={headRepoOwner}
              pullRequestGlobalRelayId={pullRequestGlobalRelayId}
              repository={repository}
              selectedPathDigest={selectedPathDigest}
            />
          )
        })}
      </div>
      {shouldShowWhimsy && <PullRequestDiffsAnimation />}

      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded.
      It needs to be somewhere on the page so that it can be drawn from within the diff lines component. */}
      <DiffPlaceholder />
    </div>
  )
}

try{ PullRequestVirtualizedDiffsList.displayName ||= 'PullRequestVirtualizedDiffsList' } catch {}